import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, iRootState } from '../../../../store';
import { useAsync, useRequestHandler } from '../../../../hooks';
import Spinner from '../../../../components_/Spinner';
import EmptyState from '../../../../components_/EmptyState';
import Button from '../../../../components_/Fields/Button';
import { Badge, Button as AntButton, Col, Input, Row, Select, Space, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CaretRightOutlined,
  CaretDownOutlined
} from '@ant-design/icons';
import { IShortUser } from '../../../../models/types';
import { useMtsModal } from '@mts-ds/components-react';
import Modal from '../../../../components_/Modal';
import { ButtonColors } from '@mts-ds/components/dist/types/components/button/enums';

interface IDepartment {
  id: string;
  name: string;
  parentId?: string;
  employees?: IShortUser[];
  children?: IDepartment[];
}

const mapState = (state: iRootState) => ({
  list: state.staffDepartaments.list as IDepartment[],
  employeesList: state.staffDepartaments.data.shortUsers as IShortUser[]
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getList: d.staffDepartaments.getList,
    getData: d.staffDepartaments.getData,
    updateOrCreate: d.staffDepartaments.updateOrCreate,
    remove: d.staffDepartaments.remove,
    reset: d.staffDepartaments.reset
  };
};

type ConnectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const Departments: FC<ConnectedProps> = ({
  list,
  employeesList,
  getList,
  getData,
  updateOrCreate,
  remove,
  reset
}) => {
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [editedRecord, setEditedRecord] = useState<IDepartment | null>(null);
  const [addingParentId, setAddingParentId] = useState<string | null>(null);
  const [newRecord, setNewRecord] = useState<IDepartment | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [active, setActive] = useState<IDepartment | null>(null);
  const [selectedEmployeesSet, setSelectedEmployeesSet] = useState<Set<string>>(new Set());

  const [runGetList, loadingList] = useAsync(getList);
  const [runGetEmployees, loadingEmployees] = useAsync(getData);
  const requestHandler = useRequestHandler();

  useEffect(() => {
    runGetList();
    runGetEmployees();
    return () => {
      reset();
    };
  }, []);

  const handleSave = async (record: IDepartment) => {
    const preparedRecord = {
      ...record,
      employees: record.employees?.map(emp => emp.id) || []
    };
    const isEdit = Boolean(preparedRecord.id);
    const isCreate = !isEdit;
    const error = await updateOrCreate(preparedRecord);
    requestHandler({ error, entity: 'department', isEdit, isCreate, onSuccess: runGetList });
    setEditRowId(null);
    setEditedRecord(null);
    setAddingParentId(null);
    setNewRecord(null);
  };

  // const handleDelete = async (id: string) => {
  //   const error = await remove(id);
  //   requestHandler({ error, entity: 'department', isDelete: true, onSuccess: runGetList });
  // };

  const [openDeleteModal] = useMtsModal(Modal, {
    header: 'Удалить подразделение',
    text: 'Вы действительно хотите удалить подразделение?',
    buttonTextConfirm: 'Удалить',
    ...(active && {
      onMtsModalConfirm: async () => {
        const error = await remove(active.id);

        requestHandler({ error, entity: 'department', isDelete: true, onSuccess: runGetList });
      }
    })
  });

  const handleClickDelete = (user: IDepartment) => () => {
    setActive(user);
    openDeleteModal();
  };

  const handleAdd = (parentId?: string) => {
    const newDept: IDepartment = { id: '', name: '', parentId, employees: [] };
    setAddingParentId(parentId || null);
    setNewRecord(newDept);

    if (parentId) {
      setExpandedRowKeys(prev => {
        if (!prev.includes(parentId)) {
          return [...prev, parentId];
        }
        return prev;
      });
    }
  };

  const insertNewRow = (data: IDepartment[]): IDepartment[] => {
    if (!addingParentId) {
      return newRecord ? [newRecord, ...data] : data;
    }
    return data.map(item => {
      if (item.id === addingParentId) {
        return {
          ...item,
          children: item.children ? [newRecord!, ...item.children] : [newRecord!]
        };
      }
      if (item.children) {
        return { ...item, children: insertNewRow(item.children) };
      }
      return item;
    });
  };

  const findRowLevel = (data: IDepartment[], targetId: string, level = 0): number | null => {
    for (const item of data) {
      if (item.id === targetId) {
        return level;
      }
      if (item.children) {
        const childLevel = findRowLevel(item.children, targetId, level + 1);
        if (childLevel !== null) {
          return childLevel;
        }
      }
    }
    return null;
  };

  const columns: ColumnsType<IDepartment> = [
    {
      title: 'Подразделение',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (text: string, record: IDepartment) => {
        const isEditing = editRowId === record.id || record === newRecord;

        if (isEditing) {
          // находим уровень вложенности строки
          const level = findRowLevel(list, record.id) || 0;

          // рассчитываем общий отступ слева
          const totalIndent = 15 * level + 24;

          return (
            // <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Input
              placeholder="Название подразделения"
              value={record === newRecord ? newRecord.name : editedRecord?.name}
              onChange={e => {
                const value = e.target.value;
                record === newRecord
                  ? setNewRecord(prev => prev && { ...prev, name: value })
                  : setEditedRecord(prev => prev && { ...prev, name: value });
              }}
              style={{ width: `calc(100% - ${totalIndent}px)` }}
            />
            // </div>
          );
        }

        // Если не редактируем, возвращаем просто текст
        return text;
      }
    },
    {
      title: 'Сотрудники',
      dataIndex: 'employees',
      key: 'employees',
      render: (_: IShortUser[], record: IDepartment) => {
        const isEditing = editRowId === record.id || record === newRecord;

        const editingEmployees =
          record === newRecord ? newRecord?.employees : editedRecord?.employees;
        const viewingEmployees = record.employees || [];

        const currentEmployees = isEditing ? editingEmployees || [] : viewingEmployees;

        if (!isEditing && currentEmployees.length !== 0) {
          return (
            <Badge overflowCount={9999} count={currentEmployees.length} />
            // <Select
            //     mode="multiple"
            //     disabled={!isEditing}
            //     allowClear
            //     style={{ width: '100%' }}
            //     maxTagCount={0}
            //     virtual={true}
            //     showSearch
            //     optionFilterProp="label"
            //     placeholder="Выберите сотрудников"
            //     filterOption={(input, option) =>
            //         (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            //     }
            //     value={currentEmployees.map(emp => String(emp.id))}
            //     onChange={(value: string[]) => {
            //       const selected = employeesList.filter(emp => value.includes(String(emp.id)));
            //
            //       if (record === newRecord) {
            //         setNewRecord(prev => ({ ...(prev || record), employees: selected }));
            //       } else {
            //         setEditedRecord(prev => ({ ...(prev || record), employees: selected }));
            //       }
            //     }}
            //     options={employeesList
            //         .map(emp => ({ label: emp.fullName, value: String(emp.id) }))
            //         .sort((a, b) => {
            //           const selectedValues = currentEmployees.map(emp => String(emp.id));
            //           const aSelected = selectedValues.includes(a.value);
            //           const bSelected = selectedValues.includes(b.value);
            //
            //           if (aSelected === bSelected) {
            //             return a.label.localeCompare(b.label); // если оба выбраны или оба не выбраны, сортируем по алфавиту
            //           }
            //
            //           return aSelected ? -1 : 1; // выбранные идут наверх
            //         })}
            // />
          );
        }

        const handleEmployeeChange = (value: string[]) => {
          // Создаем новый Set, чтобы обновить состояние
          const newSelectedEmployeesSet = new Set(selectedEmployeesSet);

          // Добавляем все новые выбранные элементы в Set
          value.forEach(id => newSelectedEmployeesSet.add(id));

          setSelectedEmployeesSet(newSelectedEmployeesSet);

          const selected = employeesList.filter(emp => value.includes(String(emp.id)));

          if (record === newRecord) {
            setNewRecord(prev => ({ ...(prev || record), employees: selected }));
          } else {
            setEditedRecord(prev => ({ ...(prev || record), employees: selected }));
          }
        };

        return (
          <Select
            key={record.id}
            mode="multiple"
            disabled={!isEditing}
            allowClear
            style={{ width: '100%' }}
            maxTagCount={0}
            virtual={true}
            showSearch
            optionFilterProp="label"
            placeholder="Выберите сотрудников"
            filterOption={(input, option) => {
              const label = String((option as any)?.label ?? '').toLowerCase();
              const query = String(input ?? '').toLowerCase();
              return label.includes(query);
            }}
            value={currentEmployees.map(emp => String(emp.id))}
            onChange={handleEmployeeChange}
            options={employeesList
              .map(emp => ({ label: emp.fullName, value: String(emp.id) }))
              .sort((a, b) => {
                const aSelected = selectedEmployeesSet.has(a.value);
                const bSelected = selectedEmployeesSet.has(b.value);

                if (aSelected === bSelected) {
                  return a.label.localeCompare(b.label); // если оба выбраны или оба не выбраны, сортируем по алфавиту
                }

                return aSelected ? -1 : 1; // выбранные идут наверх
              })}
          />
        );
      }
    },
    {
      key: 'actions',
      align: 'right',
      width: '10%',
      render: (_: any, record: IDepartment) => (
        <Space>
          {editRowId === record.id || record === newRecord ? (
            <>
              <Tooltip placement="top" title="Сохранить">
                <AntButton
                  icon={<SaveOutlined />}
                  type={'text'}
                  onClick={() => handleSave(record === newRecord ? newRecord! : editedRecord!)}
                />
              </Tooltip>
              <Tooltip placement="top" title="Отмена">
                <AntButton
                  icon={<CloseOutlined />}
                  type={'text'}
                  onClick={() => {
                    setEditRowId(null);
                    setAddingParentId(null);
                    setNewRecord(null);
                    setEditedRecord(null);
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip placement="top" title="Редактировать">
                <AntButton
                  type={'text'}
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditRowId(record.id);
                    setEditedRecord(record);
                    setSelectedEmployeesSet(new Set(record?.employees?.map(emp => String(emp.id))));
                  }}
                />
              </Tooltip>
              <Tooltip placement="top" title="Добавить дочернее">
                <AntButton
                  type={'text'}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    handleAdd(record.id);
                    setSelectedEmployeesSet(new Set());
                  }}
                />
              </Tooltip>
              <Tooltip placement="top" title="Удалить">
                <AntButton
                  type={'text'}
                  danger
                  onClick={handleClickDelete(record)}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  if (loadingList || loadingEmployees) {
    return <Spinner />;
  }
  if (!list) {
    return <EmptyState variant="serverError" />;
  }

  return (
    <>
      <Row justify="end">
        <Col style={{ marginBottom: 16 }}>
          <Button icon="plus" color={'primary' as ButtonColors} onClick={() => handleAdd()}>
            Добавить подразделение
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Table
            className="department-table"
            columns={columns}
            // bordered
            size={'middle'}
            dataSource={insertNewRow(list)}
            rowKey={record => record.id || 'new'}
            pagination={false}
            expandable={{
              childrenColumnName: 'children',
              expandIconColumnIndex: 0,
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys(prev => {
                  if (expanded) {
                    return [...prev, record.id];
                  } else {
                    return prev.filter(key => key !== record.id);
                  }
                });
              },
              expandIcon: ({ expanded, onExpand, record }) =>
                record.children ? (
                  expanded ? (
                    <CaretDownOutlined
                      style={{
                        marginRight: 8
                      }}
                      onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                        onExpand(record, e)
                      }
                    />
                  ) : (
                    <CaretRightOutlined
                      style={{
                        marginRight: 8
                      }}
                      onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                        onExpand(record, e)
                      }
                    />
                  )
                ) : (
                  <span style={{ marginRight: 16 }} />
                )
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default connect(mapState, mapDispatch)(Departments);
